import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { useLazyGetMeQuery } from '../../../../../store/user/user'
import { resetCurrentUser, setCurrentUser, setCurrentUserLoading } from '../../../../../store/meetingSummary/user'
import {
  useLazyGetRoomQuery,
  useLazyGetRoomSessionQuery,
  useLazyListAIFeaturesQuery,
  useLazyListAiResultsQuery,
  useLazyListFilesQuery,
  useLazyListRoomEventsQuery,
} from '../../../../../store/media/media'
import {
  resetRoomSession,
  selectRoomSessionCreatedBy,
  setRoomSession,
  setRoom,
  setRoomSessionHasTranscription,
  setRoomSessionIsLoading,
} from '../../../../../store/meetingSummary/roomSession'
import { addExecutedAiFeatureEvents, resetExecutedAiFeatures, setExecutedAiFeaturesLoading } from '../../../../../store/meetingSummary/executedAiFeatures'
import { useLazyGetDefaultMeetingTemplateQuery, useLazyGetMeetingTemplatesQuery } from '../../../../../store/ai/meetingTemplate'
import {
  resetMeetingTemplates,
  selectMeetingTemplates,
  selectSelectedMeetingTemplate,
  setDefaultMeetingTemplate,
  setMeetingTemplates,
  setMeetingTemplatesLoading,
} from '../../../../../store/meetingSummary/meetingTemplates'
import { resetAiFeatures, selectAiFeatures, setAiFeatures, setAiFeaturesLoading } from '../../../../../store/meetingSummary/aiFeatures'
import { useLazyGetPresetsQuery } from '../../../../../store/ai/ai'
import { resetAiFeaturePresets, setAiFeaturePresets, setAiFeaturePresetsLoading } from '../../../../../store/meetingSummary/aiFeaturePresets'
import { addAiResults, resetAiResults, setAiResultsLoading } from '../../../../../store/meetingSummary/aiResults'
import { useLazyGetAllTasksQuery, useLazyGetCommentsQuery, useLazyGetMeetingNotesQuery } from '../../../../../store/meetingSupport/meetingSupport'
import {
  resetComments,
  resetMeetingNotes,
  resetResultExplainer,
  setComments,
  setCommentsLoading,
  setMeetingNote,
  setMeetingNoteId,
  setMeetingNoteLoading,
} from '../../../../../store/meetingSummary/meetingSupport'
import { getEmptyEditorState } from '../MeetingResults/Lexical/utils'
import { FileKind } from '../../../../../models/File'
import { FileModel } from '@zocks-communications/client-sdk-js'
import { resetDisplay } from '../../../../../store/meetingSummary/display'
import {
  resetInputParametersHierarchy,
  setInputParametersHierarchyForMeetingTemplate,
  setInputParametersHierarchyLoading,
} from '../../../../../store/meetingSummary/inputParametersHierarchy'
import { useLazyGetInputParametersHierarchyNodeByPresetsQuery } from '../../../../../store/ai/inputParametersHierarchy'
import { resetTasks, setTasks, setTasksLoading } from '../../../../../store/task/task'

export const MeetingSummaryInitialization: React.FC = () => {
  const { roomId = '', sessionId = '' } = useParams()
  const accountId = useAppSelector(selectAccountId)

  useInitializeCurrentUser(accountId)
  useInitializeRoomSession(accountId, roomId, sessionId)
  useInitializeExecutedAiFeatures(accountId, roomId, sessionId)
  useInitializeAiFeatures(accountId, roomId, sessionId)
  useInitializeMeetingTemplates(accountId, roomId, sessionId)
  useInitializeInputParametersHierarchy(accountId, roomId, sessionId)
  useInitializeAiFeaturePresets(accountId)
  useInitializeAiResults(accountId, roomId, sessionId)
  useInitializeMeetingSupport(accountId, roomId, sessionId)
  useCleanupResultExplainer()
  useCleanupDisplay()
  useInitializeMeetingTasks(accountId, roomId, sessionId)

  return null
}

const useInitializeCurrentUser = (accountId: string) => {
  const dispatch = useAppDispatch()

  const [triggerGetMe] = useLazyGetMeQuery()

  useEffect(() => {
    const fetchMe = async () => {
      dispatch(setCurrentUserLoading(true))
      try {
        const { data: currentUser } = await triggerGetMe({ accountId }, true)
        if (!currentUser) {
          return
        }
        dispatch(setCurrentUser(currentUser))
      } finally {
        dispatch(setCurrentUserLoading(false))
      }
    }
    fetchMe()
    return () => {
      dispatch(resetCurrentUser())
    }
  }, [dispatch, accountId, triggerGetMe])
}
const useInitializeRoomSession = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const [triggerGetRoom] = useLazyGetRoomQuery()
  const [triggerGetRoomSession] = useLazyGetRoomSessionQuery()
  const [triggerListFiles] = useLazyListFilesQuery()

  useEffect(() => {
    const fetchRoomSession = async () => {
      dispatch(setRoomSessionIsLoading(true))
      try {
        const [{ data: room }, { data: roomSession }, { data: files }] = await Promise.all([
          triggerGetRoom({ accountId, roomId }),
          triggerGetRoomSession(
            {
              accountId,
              roomId,
              sessionId,
            },
            true,
          ),
          triggerListFiles({
            accountId,
            roomId,
            sessionId,
            kind: FileKind.TRANSCRIPTION,
            pageRequest: { page: 0, size: 500 },
          }),
        ])

        if (room) {
          dispatch(setRoom(room))
        }

        if (roomSession?.data) {
          dispatch(setRoomSession(roomSession.data))
        }

        if (files) {
          const isSessionTranscriptionFile = (file: FileModel) => {
            const fileMetadata = JSON.parse(file.metadata)
            return fileMetadata.transcriptionType === 'session'
          }
          const roomSessionHasTranscription = files.data.some(isSessionTranscriptionFile)
          dispatch(setRoomSessionHasTranscription(roomSessionHasTranscription))
        }
      } finally {
        dispatch(setRoomSessionIsLoading(false))
      }
    }
    fetchRoomSession()
    return () => {
      dispatch(resetRoomSession())
    }
  }, [dispatch, accountId, roomId, sessionId, triggerGetRoom, triggerGetRoomSession, triggerListFiles])
}
const useInitializeExecutedAiFeatures = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const [triggerGetRoomEvents] = useLazyListRoomEventsQuery()

  useEffect(() => {
    const fetchExecutedAiFeatures = async () => {
      dispatch(setExecutedAiFeaturesLoading(true))
      try {
        const { data: roomEvents } = await triggerGetRoomEvents(
          {
            accountId,
            roomId,
            sessionId,
            pageRequest: { page: 0, size: 500 },
            types: ['aiFeatureExecutionStarted', 'userAllocationUpdated'],
          },
          true,
        )
        if (!roomEvents?.data) {
          return
        }
        dispatch(addExecutedAiFeatureEvents(roomEvents.data))
      } finally {
        dispatch(setExecutedAiFeaturesLoading(false))
      }
    }
    fetchExecutedAiFeatures()
    return () => {
      dispatch(resetExecutedAiFeatures())
    }
  }, [dispatch, accountId, roomId, sessionId, triggerGetRoomEvents])
}

const useInitializeMeetingTemplates = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const [triggerGetMeetingTemplates] = useLazyGetMeetingTemplatesQuery()
  const [triggerGetDefaultMeetingTemplate] = useLazyGetDefaultMeetingTemplateQuery()
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  useEffect(() => {
    const fetchMeetingTemplates = async () => {
      dispatch(setMeetingTemplatesLoading(true))
      try {
        const [{ data: meetingTemplates }, { data: defaultMeetingTemplate }] = await Promise.all([
          triggerGetMeetingTemplates(
            {
              accountId,
              pageRequest: { page: 0, size: 500 },
              includeInactive: true,
            },
            true,
          ),
          triggerGetDefaultMeetingTemplate(
            {
              accountId,
              userId: currentUserId,
            },
            true,
          ),
        ])

        if (meetingTemplates?.data) {
          dispatch(setMeetingTemplates(meetingTemplates.data))
        }

        if (defaultMeetingTemplate) {
          dispatch(setDefaultMeetingTemplate(defaultMeetingTemplate))
        }
      } finally {
        dispatch(setMeetingTemplatesLoading(false))
      }
    }
    fetchMeetingTemplates()
    return () => {
      dispatch(resetMeetingTemplates())
    }
  }, [dispatch, triggerGetMeetingTemplates, triggerGetDefaultMeetingTemplate, accountId, roomId, sessionId, currentUserId])
}

const useInitializeAiFeatures = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const [triggerGetAiFeatures] = useLazyListAIFeaturesQuery()

  useEffect(() => {
    const fetchAiFeatures = async () => {
      dispatch(setAiFeaturesLoading(true))
      try {
        const { data: aiFeatures } = await triggerGetAiFeatures(
          {
            accountId,
            roomId,
            sessionId,
            pageRequest: { page: 0, size: 500 },
          },
          true,
        )
        if (!aiFeatures?.data) {
          return []
        }
        dispatch(setAiFeatures(aiFeatures.data))
      } finally {
        dispatch(setAiFeaturesLoading(false))
      }
    }
    fetchAiFeatures()
    return () => {
      dispatch(resetAiFeatures())
    }
  }, [dispatch, accountId, roomId, sessionId, triggerGetAiFeatures])
}

const useInitializeAiFeaturePresets = (accountId: string) => {
  const dispatch = useAppDispatch()

  const meetingTemplates = useAppSelector(selectMeetingTemplates)
  const aiFeatures = useAppSelector(selectAiFeatures)

  const [triggerGetPresets] = useLazyGetPresetsQuery()

  useEffect(() => {
    if (!meetingTemplates.length || !aiFeatures.length) {
      return
    }
    const fetchAiFeaturePresets = async () => {
      const aiFeaturePresetIds = aiFeatures.flatMap((aiFeature) => (aiFeature.preset ? [aiFeature.preset] : [])).map((preset) => preset.id)
      const uninstalledMeetingTemplatePresetIds = meetingTemplates
        .flatMap((meetingTemplate) => meetingTemplate.presets)
        .filter((meetingTemplatePreset) => !meetingTemplatePreset.aiFeatureId)
        .map((meetingTemplatePreset) => meetingTemplatePreset.aiFeaturePresetId)
      const presetIds = [...new Set([...aiFeaturePresetIds, ...uninstalledMeetingTemplatePresetIds])]
      dispatch(setAiFeaturePresetsLoading(true))
      try {
        const { data: presetsData } = await triggerGetPresets({
          accountId,
          ids: presetIds,
          pageRequest: { page: 0, size: 500 },
        })
        if (!presetsData?.data) {
          return
        }
        dispatch(setAiFeaturePresets(presetsData.data))
      } catch (e: unknown) {
        console.error(e)
      } finally {
        dispatch(setAiFeaturePresetsLoading(false))
      }
    }
    fetchAiFeaturePresets()
    return () => {
      dispatch(resetAiFeaturePresets())
    }
  }, [dispatch, accountId, meetingTemplates, aiFeatures, triggerGetPresets])
}

const useInitializeAiResults = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const [triggerGetAiResults] = useLazyListAiResultsQuery()

  useEffect(() => {
    const fetchResults = async () => {
      dispatch(setAiResultsLoading(true))
      try {
        const { data: aiResults } = await triggerGetAiResults({
          accountId,
          roomId,
          sessionId,
          pageRequest: { page: 0, size: 500 },
        })
        if (!aiResults?.data) {
          return
        }
        dispatch(addAiResults(aiResults.data))
      } finally {
        dispatch(setAiResultsLoading(false))
      }
    }
    fetchResults()
    return () => {
      dispatch(resetAiResults())
    }
  }, [dispatch, accountId, roomId, sessionId, triggerGetAiResults])
}

const useInitializeInputParametersHierarchy = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const roomSessionCreatedBy = useAppSelector(selectRoomSessionCreatedBy)

  const [triggerGetInputParametersHierarchyNodeByPresets] = useLazyGetInputParametersHierarchyNodeByPresetsQuery()

  useEffect(() => {
    if (!roomSessionCreatedBy || !selectedMeetingTemplate) {
      return
    }
    const fetchInputParametersHierarchyNodeByPresetsForMeetingTemplate = async () => {
      dispatch(setInputParametersHierarchyLoading(true))
      try {
        const { id: meetingTemplateId } = selectedMeetingTemplate
        const aiFeaturePresetIds = selectedMeetingTemplate.presets.map((meetingTemplatePreset) => meetingTemplatePreset.aiFeaturePresetId)
        const { data: inputParametersHierarchyNodeByPresets } = await triggerGetInputParametersHierarchyNodeByPresets(
          {
            accountId,
            type: 'ai-feature-presets',
            typeIds: aiFeaturePresetIds,
            effective: true,
            meetingTemplateId: selectedMeetingTemplate.id,
            userId: roomSessionCreatedBy.type === 'user' ? roomSessionCreatedBy.id : undefined,
          },
          true,
        )
        if (!inputParametersHierarchyNodeByPresets) {
          return
        }
        dispatch(
          setInputParametersHierarchyForMeetingTemplate({
            meetingTemplateId,
            inputParametersHierarchyNodeByPresets,
          }),
        )
      } finally {
        dispatch(setInputParametersHierarchyLoading(false))
      }
    }
    fetchInputParametersHierarchyNodeByPresetsForMeetingTemplate()
    return () => {
      dispatch(resetInputParametersHierarchy())
    }
  }, [dispatch, accountId, roomId, sessionId, roomSessionCreatedBy, selectedMeetingTemplate, triggerGetInputParametersHierarchyNodeByPresets])
}

const useInitializeMeetingSupport = (accountId: string, roomId: string, sessionId: string) => {
  useInitializeMeetingNote(accountId, roomId, sessionId)
  useInitializeComments(accountId, roomId, sessionId)
}

const useInitializeMeetingNote = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const [triggerGetMeetingNotes] = useLazyGetMeetingNotesQuery()

  useEffect(() => {
    const fetchMeetingNotes = async () => {
      dispatch(setMeetingNoteLoading(true))
      try {
        const { data: meetingNotes } = await triggerGetMeetingNotes(
          {
            accountId,
            roomId,
            sessionId,
            pageRequest: { page: 0, size: 500 },
          },
          true,
        )
        if (!meetingNotes?.length) {
          const emptyNote = getEmptyEditorState()
          dispatch(setMeetingNote(emptyNote))
          return
        }
        const { id, content } = meetingNotes[0]
        dispatch(setMeetingNoteId(id))
        dispatch(setMeetingNote(meetingNotes[0].format === 'rich_text' ? content : getEmptyEditorState()))
      } finally {
        dispatch(setMeetingNoteLoading(false))
      }
    }
    fetchMeetingNotes()
    return () => {
      dispatch(resetMeetingNotes())
    }
  }, [dispatch, accountId, roomId, sessionId, triggerGetMeetingNotes])
}
const useInitializeComments = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const [triggerGetComments] = useLazyGetCommentsQuery()

  useEffect(() => {
    const fetchComments = async () => {
      dispatch(setCommentsLoading(true))
      try {
        const { data: comments } = await triggerGetComments(
          {
            accountId,
            roomId,
            sessionId,
            pageRequest: { page: 0, size: 500 },
          },
          true,
        )
        if (!comments) {
          return
        }
        dispatch(setComments(comments))
      } finally {
        dispatch(setCommentsLoading(false))
      }
    }
    fetchComments()
    return () => {
      dispatch(resetComments())
    }
  }, [dispatch, accountId, roomId, sessionId, triggerGetComments])
}

const useInitializeMeetingTasks = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const [triggerGetTasks] = useLazyGetAllTasksQuery()

  useEffect(() => {
    const fetchTasks = async () => {
      dispatch(setTasksLoading(true))
      try {
        const filters = { type: 'meeting', roomId, sessionId }
        const { data: tasks } = await triggerGetTasks({ accountId, filters }).unwrap()
        dispatch(setTasks(tasks))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setTasksLoading(false))
      }
    }

    fetchTasks()

    return () => {
      dispatch(resetTasks())
    }
  }, [accountId, dispatch, roomId, sessionId, triggerGetTasks])
}

const useCleanupResultExplainer = () => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    return () => {
      dispatch(resetResultExplainer())
    }
  }, [dispatch])
}

const useCleanupDisplay = () => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    return () => {
      dispatch(resetDisplay())
    }
  }, [dispatch])
}
